import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Wand2, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * AIDescriptionButton — génère un TITRE + DESCRIPTION via Claude.
 * Props :
 *   - sourceText: string   → texte brut à améliorer (obligatoire au 1er appel)
 *   - type: 'MAAG' | 'SPECIFIQUE'
 *   - lot: string (optionnel)
 *   - onResult: ({titre, description}) => void
 */
const AIDescriptionButton = ({ sourceText, type = 'MAAG', lot = '', onResult }) => {
  const [loading, setLoading] = useState(false);
  const [previousOutput, setPreviousOutput] = useState(null);
  const [showRefine, setShowRefine] = useState(false);
  const [refineText, setRefineText] = useState('');

  const call = async ({ refine = false } = {}) => {
    if (!sourceText || sourceText.trim().length < 3) {
      toast.error("Saisis d'abord quelques mots dans la désignation ou la description");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const body = { text: sourceText, type, lot };
      if (refine && previousOutput && refineText.trim()) {
        body.previous_output = previousOutput;
        body.refine_instructions = refineText.trim();
      }
      const res = await axios.post(`${BACKEND}/api/ai/generate-description`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000  // 90s for Claude
      });
      setPreviousOutput(res.data.raw);
      onResult?.({ titre: res.data.titre, description: res.data.description });
      toast.success(refine ? 'Version corrigée' : 'Descriptif généré');
      if (refine) {
        setRefineText('');
        setShowRefine(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur IA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => call()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-medium rounded-lg hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 shadow-sm"
          data-testid="ai-generate-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {previousOutput ? 'Regénérer' : 'Améliorer avec IA'}
        </button>
        {previousOutput && (
          <button
            type="button"
            onClick={() => setShowRefine(v => !v)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-violet-300 text-violet-700 text-xs font-medium rounded-lg hover:bg-violet-50"
            data-testid="ai-refine-toggle"
          >
            <Wand2 className="w-3.5 h-3.5" />
            Corriger
          </button>
        )}
      </div>
      
      {showRefine && previousOutput && (
        <div className="flex gap-1.5 items-start bg-violet-50 border border-violet-200 rounded-lg p-2">
          <input
            type="text"
            value={refineText}
            onChange={(e) => setRefineText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); call({ refine: true }); } }}
            placeholder="Ex: plus concis, ajoute la dépose, style plus pro…"
            className="flex-1 h-8 px-2 text-xs border border-violet-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
            data-testid="ai-refine-input"
            autoFocus
          />
          <button
            type="button"
            onClick={() => call({ refine: true })}
            disabled={loading || !refineText.trim()}
            className="h-8 px-3 bg-violet-600 text-white text-xs font-medium rounded hover:bg-violet-700 disabled:opacity-60"
            data-testid="ai-refine-submit"
          >
            Appliquer
          </button>
          <button
            type="button"
            onClick={() => { setShowRefine(false); setRefineText(''); }}
            className="h-8 w-8 flex items-center justify-center text-slate-500 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AIDescriptionButton;
